import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'

let renderCallback: (() => void) | null = null

/**
 * 렌더링 콜백 함수 설정
 * @param callback 실행할 콜백 함수
 */
export const setRenderCallback = (callback: () => void) => {
	renderCallback = callback
}

/**
 * ID 문자열에서 'prefix-' 접두사 제거
 * @param s 원본 문자열
 * @returns 접두사가 제거된 문자열
 */
export const deletePrefix = (s: string) => {
	if (!s) return ''
	return s.replace('prefix-', '')
}

/**
 * 문자열에 'prefix-' 접두사 추가
 * @param s 원본 문자열
 * @returns 접두사가 추가된 문자열
 */
export const addPrefix = (s: string) => {
	if (!s) return ''
	if (s.startsWith('prefix-')) return s
	if (s.startsWith('temp-')) return s
	return `prefix-${s}`
}

/**
 * 요소의 데이터 속성을 기반으로 데이터 타입 객체 생성
 * @param target 대상 요소
 * @param size 크기 정보 (width, height)
 * @returns 데이터 타입 객체
 */
export const setDataType = (target: HTMLElement, size: { width: number[]; height: number[] } = { width: [], height: [] }) => {
	const dataType: Record<string, string> = {}
	// dataset의 모든 속성을 dataType 객체에 복사
	Object.entries(target.dataset).forEach(([key, value]) => {
		if (key && value) {
			dataType[key] = value
		}
	})

	// 첫 번째 셀(1행 1열)인 경우 전체 테이블 크기 정보를 포함
	const td = target.closest<HTMLTableCellElement>('td')
	const tr = target.closest<HTMLTableRowElement>('tr')
	if (td && tr && td.cellIndex === 1 && tr.rowIndex === 1) {
		dataType.size = JSON.stringify(size)
	}

	return dataType
}

/**
 * 블록의 내용을 확인하고 불필요한 노드 정리
 * @returns 데이터가 비어있는지 여부
 */
export const checkBlock = () => {
	const { selector } = storeToRefs(useEditorStore())
	const brTags = selector.value.querySelectorAll('br')

	// 텍스트 내용이 없는 노드 제거 (이미지 포함 노드는 제외)
	selector.value.childNodes.forEach((node: ChildNode) => {
		if (node instanceof HTMLElement && !node.textContent && !node.querySelector('img')) {
			node.remove()
		}
	})

	// br 태그 제거
	brTags.forEach((br: HTMLBRElement) => br.remove())
	let data = selector.value.innerHTML
	const INVISIBLES = /[\u200B-\u200D\uFEFF\u00A0]/g
	// 보이지 않는 문자 제거 후 데이터 확인
	if (data) {
		data = data.replace(INVISIBLES, '')
	}

	return data === ''
}

/**
 * 요소에 배경색이나 배경 이미지가 있는지 확인
 * @param el 확인할 HTMLElement
 * @returns 배경 존재 여부
 */
export const hasBackground = (el: HTMLElement) => {
	const styles = window.getComputedStyle(el)
	const bgColor = styles.backgroundColor
	const bgImage = styles.backgroundImage
	// 투명하거나 배경이 없는 경우 체크
	const transparent = !bgColor || bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent'
	const hasImage = !!bgImage && bgImage !== 'none'
	return !transparent || hasImage
}

/**
 * 요소를 래핑 해제하고 자식 노드를 부모로 이동
 * @param el 래핑 해제할 요소
 */
export const unwrap = (el: HTMLElement) => {
	const parent = el.parentNode
	if (!parent) return
	// 자식 노드들을 부모 노드로 이동시키고 현재 요소 제거
	while (el.firstChild) parent.insertBefore(el.firstChild, el)
	parent.removeChild(el)
}

/**
 * 배경색이 있는 span 태그 제거 (재귀적)
 * @param node 처리할 노드
 */
export const removeBackgroundSpan = (node: Node) => {
	if (node.nodeType === 1) {
		const el = node as HTMLElement
		// 배경이 있는 SPAN 태그인 경우 unwrap 실행
		if (el.tagName === 'SPAN' && hasBackground(el)) {
			unwrap(el)
			return
		}
		// 자식 노드들에 대해 재귀적으로 수행
		Array.from(el.children).forEach(child => removeBackgroundSpan(child))
	}
}

/**
 * 노드의 경로(인덱스 배열)를 계산
 * @param node 대상 노드
 * @returns 경로 배열
 */
export const getNodePath = (node: Node): number[] => {
	const path: number[] = []
	let currentNode: Node | null = node
	while (currentNode && currentNode.parentNode) {
		if (currentNode.nodeType !== 3) {
			// 에디터 블록이나 TD에 도달하면 경로 계산 종료
			if (currentNode instanceof Element && (currentNode.classList.contains('editor--block') || currentNode.nodeName === 'TD')) {
				path.unshift(0)
				break
			}
		}
		const parent: Node = currentNode.parentNode
		// 형제 노드들 사이에서의 인덱스 계산
		const index = Array.prototype.indexOf.call(parent.childNodes, currentNode)
		path.unshift(index)
		currentNode = parent
	}
	return path
}

/**
 * 경로를 통해 노드 찾기
 * @param root 루트 노드
 * @param path 경로 배열
 * @returns 찾은 노드
 */
export const getNodeFromPath = (root: Node, path: number[]): Node | null => {
	let node: Node = root

	for (const index of path) {
		if (!node.childNodes[index]) return node
		if (node.childNodes[index].nodeType !== 3) {
			node = node.childNodes[index]
		}
	}

	const lastIndex = path[path.length - 1]
	return node.childNodes[lastIndex] || null
}

/**
 * 에디터 핸들러 숨기기
 */
export const handlerClose = () => {
	const handler: HTMLDivElement | any = document.querySelector('.editor--handler')
	if (handler) {
		handler.style.display = 'none'
	}
}

/**
 * 에디터 핸들러 메뉴 숨기기
 */
export const handlerMenuClose = () => {
	const handler: HTMLDivElement | any = document.querySelector('.editor--handler__menu')
	if (handler) {
		handler.style.display = 'none'
	}
}
